<template>
  <div>
    <div v-if="loading" class="center-align mt-25">
      <i class="fas fa-spinner fa-spin fa-2x grey-text"></i>
      <p class="grey-text">Loading rebate report...</p>
    </div>

    <div v-else-if="report">
      <div class="row">
        <div class="col s12 xl8">
          <div v-for="cat in categories" :key="cat.key" class="card-panel">
            <div class="rebate-category-header">
              <h3 class="rebate-category-title grey-text text-darken-2">
                {{ cat.label }}
                <span v-if="cat.optional" class="grey-text small-font">(optional)</span>
              </h3>
              <span class="badge-grey-outlined">{{ assetCount(cat.key) }} / 5</span>
            </div>

            <div class="row mt-10" v-if="assetsForCategory(cat.key).length">
              <div v-for="asset in assetsForCategory(cat.key)" :key="asset.id" class="col s6 m4 l3">
                <div class="rebate-thumb">
                  <img v-if="asset.preview_url" :src="asset.preview_url" class="responsive-img" />
                  <div v-else class="rebate-file-icon center-align">
                    <i class="fas fa-file-pdf fa-2x grey-text"></i>
                    <br>
                    <small class="grey-text truncate">{{ asset.filename }}</small>
                  </div>
                  <a v-if="report.editable"
                     class="btn-floating btn-small red rebate-delete-btn"
                     @click="deleteAsset(asset.id)" :disabled="deleting">
                    <i class="fas fa-times"></i>
                  </a>
                </div>
              </div>
            </div>

            <div v-else class="grey-text mt-10 mb-10">
              <i class="fas fa-image"></i> No files uploaded yet
            </div>

            <input type="file" :ref="'file_' + cat.key"
                   @change="uploadFile(cat.key, $event)"
                   accept="image/jpeg,image/png,image/heic,application/pdf"
                   style="display:none">
            <button v-if="report.editable"
                    class="btn-small outlined-btn"
                    @click="triggerUpload(cat.key)"
                    :disabled="assetCount(cat.key) >= 5 || uploading">
              <i class="fas fa-plus"></i> Add File
            </button>
          </div>
        </div>

        <div class="col s12 m6 xl4">
          <div class="card-panel rebate-sidebar">
            <span class="badge right" :class="badgeClass">{{ report.state }}</span>
            <div class="mt-25">
              <h3 class="bold-font">Status</h3>
              <div v-for="cat in categories" :key="'count_' + cat.key" class="rebate-count-row">
                <span class="grey-text">{{ cat.label }}</span>
                <span class="right bold-font">{{ assetCount(cat.key) }}</span>
              </div>
            </div>

            <div class="mt-25">
              <button v-if="report.state === 'draft' && report.submittable"
                      class="btn waves-effect wide-btn"
                      @click="submitReport" :disabled="submitting">
                <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
                Submit Rebate
              </button>

              <button v-if="report.state === 'submitted' && report.submittable"
                      class="btn waves-effect wide-btn"
                      @click="submitReport" :disabled="submitting">
                <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
                Re-submit
              </button>

              <p v-if="report.state === 'draft' && !report.submittable" class="grey-text small-font">
                Upload at least one file to submit.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="uploading" class="progress">
        <div class="indeterminate"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    transactionId: { type: String, required: true }
  },

  data() {
    return {
      report: null,
      loading: true,
      uploading: false,
      submitting: false,
      deleting: false,
      categories: [
        { key: 'customer_invoice', label: 'Customer Invoice', optional: false },
        { key: 'old_unit', label: 'Old Unit', optional: false },
        { key: 'new_unit', label: 'New Unit', optional: false },
        { key: 'additional_proof', label: 'Additional Proof', optional: true },
        { key: 'serial_number_plate', label: 'Serial Number Plate', optional: false },
        { key: 'internal_invoice', label: 'Internal Invoice', optional: false }
      ]
    }
  },

  computed: {
    badgeClass() {
      const map = {
        draft: 'grey white-text',
        submitted: 'emerald-back white-text',
        closed: 'emerald-back white-text'
      }
      return map[this.report.state] || ''
    },

    basePath() {
      return '/api/v1/app/merchant/transactions/' + this.transactionId + '/rebate_report'
    }
  },

  mounted() {
    this.initReport()
  },

  methods: {
    initReport() {
      this.loading = true
      this.$http.get(this.basePath).then(response => {
        this.setReport(response.body.data.attributes)
      }, error => {
        if (error.status === 404) {
          this.createReport()
          return
        }
        this.errorHandler(error)
      }).finally(() => {
        this.loading = false
      })
    },

    createReport() {
      this.$http.post(this.basePath).then(response => {
        this.setReport(response.body.data.attributes)
      }, error => {
        this.errorHandler(error)
      }).finally(() => {
        this.loading = false
      })
    },

    setReport(attrs) {
      this.report = attrs
    },

    assetsForCategory(category) {
      if (!this.report || !this.report.assets) return []
      return this.report.assets.filter(a => a.category === category)
    },

    assetCount(category) {
      if (!this.report || !this.report.asset_counts) return 0
      return this.report.asset_counts[category] || 0
    },

    triggerUpload(category) {
      const refs = this.$refs['file_' + category]
      if (refs && refs[0]) refs[0].click()
    },

    uploadFile(category, event) {
      const file = event.target.files[0]
      if (!file) return

      if (file.size > 10 * 1024 * 1024) {
        this.notifyError('File is too large. Maximum size is 10MB.')
        event.target.value = ''
        return
      }

      this.uploading = true
      const formData = new FormData()
      formData.append('file', file)
      formData.append('category', category)

      this.$http.post(this.basePath + '/assets', formData).then(response => {
        this.setReport(response.body.report)
        this.notifySuccess('Image uploaded')
      }, error => {
        this.errorHandler(error)
      }).finally(() => {
        this.uploading = false
        event.target.value = ''
      })
    },

    deleteAsset(assetId) {
      this.deleting = true
      this.$http.delete(this.basePath + '/assets/' + assetId).then(response => {
        this.setReport(response.body.report)
        this.notifySuccess('Image removed')
      }, error => {
        this.errorHandler(error)
      }).finally(() => {
        this.deleting = false
      })
    },

    submitReport() {
      this.submitting = true
      const wasSubmitted = this.report.state === 'submitted'
      this.$http.post(this.basePath + '/submit').then(response => {
        this.setReport(response.body.data.attributes)
        this.notifySuccess(wasSubmitted ? 'Rebate re-submitted successfully' : 'Rebate submitted successfully')
      }, error => {
        this.errorHandler(error)
      }).finally(() => {
        this.submitting = false
      })
    }
  }
}
</script>

<style scoped>
.rebate-category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rebate-category-title {
  margin: 0 0 5px 0;
  font-family: helvetica-bold, sans-serif !important;
  font-weight: 900 !important;
  font-size: large !important;
}

.rebate-thumb {
  position: relative;
  margin-bottom: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  min-height: 80px;
}

.rebate-thumb img {
  width: 100%;
  display: block;
}

.rebate-delete-btn {
  position: absolute;
  top: 4px;
  right: 4px;
}

.rebate-file-icon {
  padding: 15px 5px;
}

.rebate-sidebar {
  position: sticky;
  top: 80px;
}

.rebate-count-row {
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
}

.rebate-count-row:last-child {
  border-bottom: none;
}
</style>
